export interface ModalOptions {
  title?: string
  content?: string
  okText?: string
  cancelText?: string
  onOk?: () => void | Promise<void>
  onCancel?: () => void | Promise<void>
  closable?: boolean
  maskClosable?: boolean
  width?: string
  className?: string
}

export interface ConfirmOptions extends ModalOptions {
  type?: 'info' | 'success' | 'error' | 'warning'
}

export interface UploadModalOptions extends ModalOptions {
  accept?: string
  maxFileSize?: number
  multiple?: boolean
  onUploaded?: (files: File[]) => void
}

let modalContainer: HTMLElement | null = null

function getContainer(): HTMLElement {
  if (!modalContainer) {
    modalContainer = document.createElement('div')
    modalContainer.className = 'prl-modal-container'
    document.body.appendChild(modalContainer)
  }
  return modalContainer
}

function createModal(options: ModalOptions = {}): HTMLElement {
  const {
    title = '提示',
    content = '',
    okText = '确定',
    cancelText = '取消',
    closable = true,
    width = '520px',
    className = '',
  } = options

  const modal = document.createElement('div')
  modal.className = `prl-modal ${className}`
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
  `

  modal.innerHTML = `
    <div class="prl-modal-mask" style="
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
    "></div>
    <div class="prl-modal-content" style="
      position: relative;
      width: ${width};
      max-width: 90%;
      background: #fff;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
      animation: prl-modal-in 0.3s ease-out;
    ">
      ${closable ? `<button class="prl-modal-close" style="
        position: absolute;
        top: 12px;
        right: 12px;
        border: none;
        background: none;
        font-size: 24px;
        cursor: pointer;
        color: #999;
        padding: 0;
        line-height: 1;
      ">×</button>` : ''}
      <div class="prl-modal-header" style="
        padding: 16px 24px;
        border-bottom: 1px solid #f0f0f0;
        font-size: 16px;
        font-weight: 500;
        color: #333;
      ">${title}</div>
      <div class="prl-modal-body" style="
        padding: 24px;
        font-size: 14px;
        color: #666;
        line-height: 1.5;
      ">${content}</div>
      <div class="prl-modal-footer" style="
        padding: 16px 24px;
        border-top: 1px solid #f0f0f0;
        display: flex;
        justify-content: flex-end;
        gap: 8px;
      ">
        ${cancelText ? `<button class="prl-modal-btn prl-modal-cancel" style="
          padding: 8px 16px;
          border: 1px solid #d9d9d9;
          background: #fff;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          color: #666;
          transition: all 0.3s;
        ">${cancelText}</button>` : ''}
        <button class="prl-modal-btn prl-modal-ok" style="
          padding: 8px 16px;
          border: 1px solid #1890ff;
          background: #1890ff;
          color: #fff;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s;
        ">${okText}</button>
      </div>
    </div>
  `

  return modal
}

function bindEvents(modal: HTMLElement, options: ModalOptions): void {
  const { onOk, onCancel, maskClosable = true } = options

  const closeBtn = modal.querySelector('.prl-modal-close')
  const cancelBtn = modal.querySelector('.prl-modal-cancel')
  const okBtn = modal.querySelector('.prl-modal-ok')
  const mask = modal.querySelector('.prl-modal-mask')

  const close = async () => {
    modal.style.animation = 'prl-modal-out 0.3s ease-out'
    setTimeout(() => modal.remove(), 300)
  }

  closeBtn?.addEventListener('click', async () => {
    await onCancel?.()
    close()
  })

  cancelBtn?.addEventListener('click', async () => {
    await onCancel?.()
    close()
  })

  okBtn?.addEventListener('click', async () => {
    await onOk?.()
    close()
  })

  if (maskClosable) {
    mask?.addEventListener('click', async () => {
      await onCancel?.()
      close()
    })
  }
}

export function modal(options: ModalOptions): void {
  const modalEl = createModal(options)
  getContainer().appendChild(modalEl)
  bindEvents(modalEl, options)
}

export function alert(content: string, title?: string, onOk?: () => void): void {
  modal({
    title: title || '提示',
    content,
    okText: '确定',
    cancelText: '',
    onOk,
  })
}

export function confirm(options: ConfirmOptions): void {
  const { type = 'info', ...rest } = options
  const icon = getTypeIcon(type)
  const content = `
    <div style="display: flex; align-items: flex-start; gap: 12px;">
      <span style="font-size: 24px; color: ${getTypeColor(type)};">${icon}</span>
      <div>${options.content}</div>
    </div>
  `
  modal({ ...rest, content })
}

function getTypeColor(type: string): string {
  const colors: Record<string, string> = {
    info: '#1890ff',
    success: '#52c41a',
    error: '#ff4d4f',
    warning: '#faad14',
  }
  return colors[type] || colors.info
}

function getTypeIcon(type: string): string {
  const icons: Record<string, string> = {
    info: 'ℹ',
    success: '✓',
    error: '✕',
    warning: '!',
  }
  return icons[type] || icons.info
}

export function prompt(title: string, defaultValue?: string): Promise<string | null> {
  return new Promise((resolve) => {
    const content = `
      <div>
        <div style="margin-bottom: 12px;">${title}</div>
        <input type="text" class="prl-prompt-input" value="${defaultValue || ''}" style="
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #d9d9d9;
          border-radius: 4px;
          font-size: 14px;
          box-sizing: border-box;
        " />
      </div>
    `

    const modalEl = createModal({
      title: '请输入',
      content,
      okText: '确定',
      cancelText: '取消',
    })

    getContainer().appendChild(modalEl)

    const input = modalEl.querySelector('.prl-prompt-input') as HTMLInputElement
    const okBtn = modalEl.querySelector('.prl-modal-ok')
    const cancelBtn = modalEl.querySelector('.prl-modal-cancel')
    const closeBtn = modalEl.querySelector('.prl-modal-close')
    const mask = modalEl.querySelector('.prl-modal-mask')

    const close = () => {
      modalEl.style.animation = 'prl-modal-out 0.3s ease-out'
      setTimeout(() => modalEl.remove(), 300)
    }

    okBtn?.addEventListener('click', () => {
      resolve(input?.value || '')
      close()
    })

    cancelBtn?.addEventListener('click', () => {
      resolve(null)
      close()
    })

    closeBtn?.addEventListener('click', () => {
      resolve(null)
      close()
    })

    mask?.addEventListener('click', () => {
      resolve(null)
      close()
    })
  })
}

export function createUploadModal(options: UploadModalOptions): Promise<File[]> {
  return new Promise((resolve) => {
    const {
      accept = '*',
      maxFileSize,
      multiple = false,
      onUploaded
    } = options

    // 创建隐藏的文件输入元素
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    input.multiple = multiple
    input.style.display = 'none'
    
    // 添加到文档中
    document.body.appendChild(input)
    
    // 处理文件选择
    input.addEventListener('change', async () => {
      const files = Array.from(input.files || [])
      
      if (maxFileSize) {
        const validFiles = files.filter(file => file.size <= maxFileSize)
        if (validFiles.length < files.length) {
          alert('部分文件大小超过限制，已自动过滤')
        }
        await onUploaded?.(validFiles)
        resolve(validFiles)
      } else {
        await onUploaded?.(files)
        resolve(files)
      }
      
      // 移除输入元素
      document.body.removeChild(input)
    })
    
    // 模拟点击打开文件选择对话框
    input.click()
  })
}


